
import React, { Suspense } from 'react';
import MainLayout from '../layouts/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { useAiProvider } from '../contexts/AiProviderContext';
import { useSiswa } from '../contexts/SiswaContext';
import { useStudentData } from '../contexts/StudentDataContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigation } from '../contexts/NavigationContext';
import { SubscriptionRoute } from '../components/common';

// Lazy Load Pages
const LandingPage = React.lazy(() => import('../pages/LandingPage'));
const PricingPage = React.lazy(() => import('../pages/PricingPage'));
const BlogPage = React.lazy(() => import('../pages/BlogPage'));
const AboutPage = React.lazy(() => import('../pages/AboutPage'));
const ContactPage = React.lazy(() => import('../pages/ContactPage'));
const PrivacyPage = React.lazy(() => import('../pages/PrivacyPage'));
const TermsPage = React.lazy(() => import('../pages/TermsPage'));
const DocsPage = React.lazy(() => import('../pages/DocsPage'));
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const DashboardPage = React.lazy(() => import('../pages/DashboardPage'));
const BantuanPage = React.lazy(() => import('../pages/BantuanPage'));
const BuatSoalPage = React.lazy(() => import('../pages/BuatSoalPage'));

const ApiKeySettingsPage = React.lazy(() => import('../pages/ApiKeySettingsPage'));
const AdminDashboardPage = React.lazy(() => import('../AdminDashboardPage'));
const AdminRombelManagementPage = React.lazy(() => import('../AdminRombelManagementPage'));
const BlogManagementPage = React.lazy(() => import('../pages/admin/BlogManagementPage'));
const BlogEditorPage = React.lazy(() => import('../pages/admin/BlogEditorPage'));
const BlogPostPage = React.lazy(() => import('../pages/BlogPostPage'));
const QualitativeDescriptionPage = React.lazy(() => import('../QualitativeDescriptionPage'));
const IntegratedScorePage = React.lazy(() => import('../IntegratedScorePage'));
const AssessmentTypeManagementPage = React.lazy(() => import('../AssessmentTypeManagementPage'));
const ScriptGeneratorPage = React.lazy(() => import('../ScriptGeneratorPage'));

const TPAnalysisPage = React.lazy(() => import('../TPAnalysisPage'));
const ModulAjarPage = React.lazy(() => import('../ModulAjarPage'));
const AIContentAssistant = React.lazy(() => import('../AIContentAssistant'));
const AIQuizGenerator = React.lazy(() => import('../components/AIQuizGenerator'));
const InteractiveQuizList = React.lazy(() => import('../components/InteractiveQuizList'));
const SimpleInteractiveQuizInterface = React.lazy(() => import('../components/SimpleInteractiveQuizInterface'));
const SimpleQuizResultAnalysis = React.lazy(() => import('../components/SimpleQuizResultAnalysis'));
const AdminPage = React.lazy(() => import('../pages/AdminPage'));
const DiferensiasiMateriPage = React.lazy(() => import('../pages/DiferensiasiMateriPage'));
const ChatAiPage = React.lazy(() => import('../pages/ChatAiPage'));
const BuatMateriAjarPage = React.lazy(() => import('../pages/BuatMateriAjarPage'));
const BuatLkpdPage = React.lazy(() => import('../pages/BuatLkpdPage'));
const BuatProtaProsemPage = React.lazy(() => import('../pages/BuatProtaProsemPage'));
const HtmlGameBuilderPage = React.lazy(() => import('../pages/HtmlGameBuilderPage'));
const BuatRubrikPenilaianPage = React.lazy(() => import('../pages/BuatRubrikPenilaianPage'));
const BuatRangkumanPage = React.lazy(() => import('../pages/BuatRangkumanPage'));
const IdeAktivitasPage = React.lazy(() => import('../pages/IdeAktivitasPage'));
const GamesPage = React.lazy(() => import('../pages/GamesPage'));
const SettingsPage = React.lazy(() => import('../pages/SettingsPage'));
const IdeMediaCanvaPage = React.lazy(() => import('../pages/IdeMediaCanvaPage'));
const BuatCeritaPage = React.lazy(() => import('../pages/BuatCeritaPage'));
const BuatKerangkaPresentasiPage = React.lazy(() => import('../pages/BuatKerangkaPresentasiPage'));
const BuatSkenarioPage = React.lazy(() => import('../pages/BuatSkenarioPage'));
const BankKontenPage = React.lazy(() => import('../pages/BankKontenPage'));
const JurnalMengajarPage = React.lazy(() => import('../JurnalMengajarPage'));
const GuruSoalPage = React.lazy(() => import('../GuruSoalPage'));
const BankSoalPage = React.lazy(() => import('../BankSoalPage'));
const GuruLKPDPage = React.lazy(() => import('../GuruLKPDPage'));
const GuruLKPDResultsPage = React.lazy(() => import('../GuruLKPDResultsPage'));
const GuruQuizManagementPage = React.lazy(() => import('../GuruQuizManagementPage'));
const SiswaNilaiPage = React.lazy(() => import('../SiswaNilaiPage'));
const SiswaSoalPage = React.lazy(() => import('../SiswaSoalPage'));
const SiswaLKPDPage = React.lazy(() => import('../SiswaLKPDPage'));
const SiswaDashboardPage = React.lazy(() => import('../SiswaDashboardPage'));
const GuruProfilPage = React.lazy(() => import('../GuruProfilPage'));
const SiswaProfilPage = React.lazy(() => import('../SiswaProfilPage'));
const SiswaMateriPage = React.lazy(() => import('../SiswaMateriPage'));
const MateriDetailPage = React.lazy(() => import('../MateriDetailPage'));
const TestMateriPage = React.lazy(() => import('../TestMateriPage'));
const GuruDashboardTerintegrasi = React.lazy(() => import('../GuruDashboardTerintegrasi'));
const RombelManagementPage = React.lazy(() => import('../RombelManagementPage'));
const RombelChatPage = React.lazy(() => import('../RombelChatPage'));
const ExamGradingDashboard = React.lazy(() => import('../ExamGradingDashboard'));
const ComprehensiveAssessmentDashboard = React.lazy(() => import('../ComprehensiveAssessmentDashboard'));
const EdutainmentIdeasGenerator = React.lazy(() => import('../components/edutainment/EdutainmentIdeasGenerator'));
const AcademicPeriodManagement = React.lazy(() => import('../components/AcademicPeriodManagement'));
const RaporCenterPage = React.lazy(() => import('../RaporCenterPage'));
const DiagnosticQuizPage = React.lazy(() => import('../pages/DiagnosticQuizPage'));
const DiagnosticQuizInterface = React.lazy(() => import('../components/DiagnosticQuizInterface'));
const CategoryMenuPage = React.lazy(() => import('../pages/CategoryMenuPage'));
const AnalisisDiagnostikViewer = React.lazy(() => import('../pages/AnalisisDiagnostikViewer'));
const PremiumScriptsPage = React.lazy(() => import('../pages/PremiumScriptsPage'));
const BankCPPage = React.lazy(() => import('../pages/BankCPPage'));
const BankTPPage = React.lazy(() => import('../pages/BankTPPage'));
const KelolaATPPage = React.lazy(() => import('../pages/KelolaATPPage'));
const RencanaAsesmenPage = React.lazy(() => import('../pages/RencanaAsesmenPage'));
const GuruRombelDetailPage = React.lazy(() => import('../pages/GuruRombelDetailPage'));
const SiswaRombelDetailPage = React.lazy(() => import('../pages/SiswaRombelDetailPage'));
const SiswaAvatarPage = React.lazy(() => import('../pages/SiswaAvatarPage'));
const SiswaBadgesPage = React.lazy(() => import('../pages/SiswaBadgesPage'));
const SiswaLeaderboardPage = React.lazy(() => import('../pages/SiswaLeaderboardPage'));
const SiswaKelasPage = React.lazy(() => import('../pages/SiswaKelasPage')); // Added import for dedicated rombel page

// Loading Fallback Component
const LoadingFallback = () => (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 font-medium">Memuat halaman...</p>
        </div>
    </div>
);

const PublicPageWrapper = ({ children }) => {
    const { setForcedTheme } = useTheme();
    // Use effect removed here in original refactor if not needed, but keeping for consistency with original file
    React.useEffect(() => {
        setForcedTheme('light');
        return () => setForcedTheme(null);
    }, [setForcedTheme]);

    return children;
};

const AppRoutes = () => {
    const { user, login, emailAuth, logout, resetPassword } = useAuth();
    const { aiModel, setAiModel } = useAiProvider();
    const { studentData } = useStudentData();

    // USE NAVIGATION CONTEXT
    const {
        page,
        pageProps,
        navigate,
        isMobileMenuOpen,
        setIsMobileMenuOpen,
        isSidebarCollapsed,
        setIsSidebarCollapsed
    } = useNavigation();

    const handleLogoutAndNavigate = async () => {
        await logout();
        navigate('login');
    };

    const renderPage = () => {
        // Role-based routing
        if (user.role === 'student') {
            switch (page) {
                case 'siswa_dashboard': return <SiswaDashboardPage user={user} setPage={navigate} />;
                case 'kelas_saya': return <SiswaKelasPage user={user} setPage={navigate} />; // Updated to dedicated page
                case 'siswa_soal': return <SiswaSoalPage user={user} setPage={navigate} />;
                case 'siswa_lkpd': return <SiswaLKPDPage user={user} setPage={navigate} />;
                case 'materi': return <SiswaMateriPage user={user} setPage={navigate} />;
                case 'materi-detail': return <MateriDetailPage user={user} pageProps={pageProps} setPage={navigate} />;
                case 'test-materi': return <TestMateriPage user={user} data={pageProps.data} setPage={navigate} />;
                case 'interactive-quiz': return <InteractiveQuizList
                    user={user}
                    onStartQuiz={(quiz) => navigate('do-quiz', { quizData: quiz })}
                    onViewQuiz={(quiz) => navigate('quiz-result', { resultId: quiz.lastSubmissionId || quiz.id })}
                />;
                case 'do-quiz': return <SimpleInteractiveQuizInterface
                    user={user}
                    quizData={pageProps.quizData}
                    quizId={pageProps.quizId || (pageProps.quizData && pageProps.quizData.id)}
                    userId={user.uid}
                    onExitQuiz={() => navigate('interactive-quiz')}
                    onQuizCompleted={(resultId) => navigate('quiz-result', { resultId })}
                />;
                case 'quiz-result': return <SimpleQuizResultAnalysis user={user} resultId={pageProps.resultId} setPage={navigate} />;
                case 'diagnostic-quiz': return <DiagnosticQuizInterface
                    quizId={pageProps.quizId}
                    onComplete={() => navigate('siswa_dashboard')}
                    onExit={() => navigate('siswa_dashboard')}
                />;
                case 'games': return <GamesPage user={user} studentData={studentData} />;
                case 'nilai': return <SiswaNilaiPage user={user} setPage={navigate} />;

                case 'profil': return <SiswaProfilPage user={user} setPage={navigate} />;
                case 'settings': return <SettingsPage user={user} setPage={navigate} />;
                case 'bantuan': return <BantuanPage user={user} setPage={navigate} />;
                case 'apikey-settings': return <ApiKeySettingsPage user={user} setPage={navigate} />;
                case 'siswa-rombel-detail': return <SiswaRombelDetailPage user={user} rombelId={pageProps.rombelId} setPage={navigate} />;
                case 'siswa-avatar': return <SiswaAvatarPage setPage={navigate} />;
                case 'siswa-badges': return <SiswaBadgesPage setPage={navigate} />;
                case 'siswa-leaderboard': return <SiswaLeaderboardPage setPage={navigate} />;
                default: return <SiswaDashboardPage user={user} setPage={navigate} />;
            }
        }

        if (user.role === 'admin') {
            switch (page) {
                case 'dashboard': return <AdminDashboardPage user={user} setPage={navigate} />;
                case 'admin': return <AdminPage user={user} setPage={navigate} />;
                case 'settings': return <SettingsPage user={user} setPage={navigate} />;
                case 'bantuan': return <BantuanPage user={user} setPage={navigate} />;
                case 'apikey-settings': return <ApiKeySettingsPage user={user} setPage={navigate} />;
                case 'admin-rombel-management': return <AdminRombelManagementPage user={user} setPage={navigate} />;
                case 'academic-period-management': return <AcademicPeriodManagement user={user} setPage={navigate} />;

                // Blog Management
                case 'admin-blog': return <BlogManagementPage setPage={navigate} />;
                case 'admin-blog-new': return <BlogEditorPage onBack={() => navigate('admin-blog')} />;
                case 'admin-blog-edit': return <BlogEditorPage onBack={() => navigate('admin-blog')} postId={pageProps.postId} />;

                default: return <AdminDashboardPage user={user} setPage={navigate} />;
            }
        }

        // Teacher Routing
        switch (page) {
            case 'category-menu': return <CategoryMenuPage navigate={navigate} user={user} />;
            case 'category-dashboard': return <CategoryMenuPage categoryId="dashboard" navigate={navigate} user={user} />;
            case 'category-soal': return <CategoryMenuPage categoryId="soal" navigate={navigate} user={user} />;
            case 'category-konten': return <CategoryMenuPage categoryId="konten" navigate={navigate} user={user} />;
            case 'category-penilaian': return <CategoryMenuPage categoryId="penilaian" navigate={navigate} user={user} />;
            case 'category-kelas': return <CategoryMenuPage categoryId="kelas" navigate={navigate} user={user} />;
            case 'category-ai_tools': return <CategoryMenuPage categoryId="ai_tools" navigate={navigate} user={user} />;
            case 'category-perencanaan': return <CategoryMenuPage categoryId="perencanaan" navigate={navigate} user={user} />;

            case 'dashboard': return <DashboardPage user={user} setPage={navigate} />;
            case 'guru_dashboard_terintegrasi': return <SubscriptionRoute user={user} navigate={navigate}><GuruDashboardTerintegrasi user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'assessment-comprehensive': return <SubscriptionRoute user={user} navigate={navigate}><ComprehensiveAssessmentDashboard user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'exam_grading': return <SubscriptionRoute user={user} navigate={navigate}><ExamGradingDashboard user={user} setPage={navigate} /></SubscriptionRoute>;

            case 'rombel-management': return <SubscriptionRoute user={user} navigate={navigate}><RombelManagementPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'rombel-detail': return <SubscriptionRoute user={user} navigate={navigate}><GuruRombelDetailPage user={user} rombelId={pageProps.rombelId} setPage={navigate} /></SubscriptionRoute>;
            case 'rombel-chat': return <SubscriptionRoute user={user} navigate={navigate}><RombelChatPage user={user} rombelId={pageProps.rombelId} setPage={navigate} /></SubscriptionRoute>;
            case 'admin-rombel': return <SubscriptionRoute user={user} navigate={navigate}><AdminRombelManagementPage user={user} setPage={navigate} /></SubscriptionRoute>;

            // Perencanaan Routes
            case 'bank-cp': return <SubscriptionRoute user={user} navigate={navigate}><BankCPPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'bank-tp': return <SubscriptionRoute user={user} navigate={navigate}><BankTPPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'kelola-atp': return <SubscriptionRoute user={user} navigate={navigate}><KelolaATPPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'rencana-asesmen': return <SubscriptionRoute user={user} navigate={navigate}><RencanaAsesmenPage user={user} setPage={navigate} /></SubscriptionRoute>;

            case 'guru_soal': return <SubscriptionRoute user={user} navigate={navigate}><GuruSoalPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'bank_soal': return <SubscriptionRoute user={user} navigate={navigate}><BankSoalPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'soal': return <SubscriptionRoute user={user} navigate={navigate}><BuatSoalPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;

            case 'guru_lkpd': return <SubscriptionRoute user={user} navigate={navigate}><GuruLKPDPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'guru_lkpd_results': return <SubscriptionRoute user={user} navigate={navigate}><GuruLKPDResultsPage user={user} lkpdId={pageProps.lkpdId} setPage={navigate} /></SubscriptionRoute>;
            case 'lkpd': return <SubscriptionRoute user={user} navigate={navigate}><BuatLkpdPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;

            case 'guru_quiz': return <SubscriptionRoute user={user} navigate={navigate}><GuruQuizManagementPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'create-quiz': return <SubscriptionRoute user={user} navigate={navigate}><AIQuizGenerator user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'interactive-quiz': return <SubscriptionRoute user={user} navigate={navigate}><InteractiveQuizList user={user} setPage={navigate} /></SubscriptionRoute>;

            case 'diagnostic-quiz': return <SubscriptionRoute user={user} navigate={navigate}><DiagnosticQuizPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'analisis-diagnostik': return <SubscriptionRoute user={user} navigate={navigate}><AnalisisDiagnostikViewer user={user} setPage={navigate} preloadedData={pageProps.preloadedData} preloadedMeta={pageProps.preloadedMeta} /></SubscriptionRoute>;
            case 'bankkonten': return <SubscriptionRoute user={user} navigate={navigate}><BankKontenPage user={user} setPage={navigate} /></SubscriptionRoute>;

            case 'prota': return <SubscriptionRoute user={user} navigate={navigate}><BuatProtaProsemPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'presentasi': return <SubscriptionRoute user={user} navigate={navigate}><BuatKerangkaPresentasiPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'rangkuman': return <SubscriptionRoute user={user} navigate={navigate}><BuatRangkumanPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'materi': return <SubscriptionRoute user={user} navigate={navigate}><BuatMateriAjarPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'diferensiasi-materi': return <SubscriptionRoute user={user} navigate={navigate}><DiferensiasiMateriPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'modul-ajar': return <SubscriptionRoute user={user} navigate={navigate}><ModulAjarPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'edutainment-generator': return <SubscriptionRoute user={user} navigate={navigate}><EdutainmentIdeasGenerator user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'rubrik': return <SubscriptionRoute user={user} navigate={navigate}><BuatRubrikPenilaianPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'cerita': return <SubscriptionRoute user={user} navigate={navigate}><BuatCeritaPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'skenario': return <SubscriptionRoute user={user} navigate={navigate}><BuatSkenarioPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;

            case 'ide': return <SubscriptionRoute user={user} navigate={navigate}><IdeAktivitasPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'media': return <SubscriptionRoute user={user} navigate={navigate}><IdeMediaCanvaPage user={user} setPage={navigate} {...pageProps} /></SubscriptionRoute>;
            case 'chat': return <SubscriptionRoute user={user} navigate={navigate}><ChatAiPage user={user} /></SubscriptionRoute>;
            case 'ai-assistant': return <SubscriptionRoute user={user} navigate={navigate}><AIContentAssistant user={user} setPage={navigate} /></SubscriptionRoute>;

            case 'script-generator': return <SubscriptionRoute user={user} navigate={navigate}><ScriptGeneratorPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'game-builder': return <SubscriptionRoute user={user} navigate={navigate}><HtmlGameBuilderPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'premium-scripts': return <SubscriptionRoute user={user} navigate={navigate}><PremiumScriptsPage user={user} setPage={navigate} /></SubscriptionRoute>;

            case 'tp-analysis': return <SubscriptionRoute user={user} navigate={navigate}><TPAnalysisPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'analisis':
                if (pageProps.resultId) {
                    return <SubscriptionRoute user={user} navigate={navigate}><SimpleQuizResultAnalysis user={user} setPage={navigate} {...pageProps} onBackToDashboard={() => navigate('analisis')} /></SubscriptionRoute>;
                }
                return <SubscriptionRoute user={user} navigate={navigate}><ComprehensiveAssessmentDashboard user={user} setPage={navigate} initialTab="analisis" /></SubscriptionRoute>;
            case 'jurnal': return <SubscriptionRoute user={user} navigate={navigate}><JurnalMengajarPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'integrated-score': return <SubscriptionRoute user={user} navigate={navigate}><IntegratedScorePage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'assessment-types': return <SubscriptionRoute user={user} navigate={navigate}><AssessmentTypeManagementPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'qualitative-description': return <SubscriptionRoute user={user} navigate={navigate}><QualitativeDescriptionPage user={user} setPage={navigate} /></SubscriptionRoute>;
            case 'rapor-center': return <SubscriptionRoute user={user} navigate={navigate}><RaporCenterPage user={user} setPage={navigate} /></SubscriptionRoute>;

            case 'guru_profil': return <GuruProfilPage user={user} setPage={navigate} />;
            case 'settings': return <SettingsPage user={user} setPage={navigate} />;
            case 'bantuan': return <BantuanPage user={user} setPage={navigate} />;
            case 'apikey-settings': return <ApiKeySettingsPage user={user} setPage={navigate} />;

            default: return <DashboardPage user={user} setPage={navigate} />;
        }
    };

    if (!user || ['landing', 'pricing', 'blog', 'blog-post', 'about', 'contact', 'privacy', 'terms', 'docs'].includes(page)) {
        if (page === 'landing') {
            return (
                <PublicPageWrapper>
                    <Suspense fallback={<LoadingFallback />}>
                        <LandingPage
                            onLogin={() => navigate('login')}
                            onNavigate={(newPage) => navigate(newPage)}
                        />
                    </Suspense>
                </PublicPageWrapper>
            );
        }

        if (page === 'login') {
            return (
                <Suspense fallback={<LoadingFallback />}>
                    <LoginPage onLogin={login} onEmailAuth={emailAuth} onResetPassword={resetPassword} />
                </Suspense>
            );
        }
        if (page === 'pricing') {
            return (
                <PublicPageWrapper>
                    <Suspense fallback={<LoadingFallback />}>
                        <PricingPage onLogin={() => navigate('login')} onBack={() => navigate('dashboard')} />
                    </Suspense>
                </PublicPageWrapper>
            );
        }
        // ... Repeated patterns for public pages (kept identical to original logic) ...
        // Simplification: Using switch for public pages to clean up code while we are here?
        // Original code used multiple ifs. Keeping it structurally similar to minimize diff noise unless refactoring logic drastically.
        // Actually, let's keep the exact same if/else block structure to avoid breaking lazy imports or specialized logic hidden in them.

        if (page === 'about') {
            return (<PublicPageWrapper><Suspense fallback={<LoadingFallback />}><AboutPage onLogin={() => navigate('login')} onBack={() => navigate('dashboard')} /></Suspense></PublicPageWrapper>);
        }
        if (page === 'contact') {
            return (<PublicPageWrapper><Suspense fallback={<LoadingFallback />}><ContactPage onLogin={() => navigate('login')} onBack={() => navigate('dashboard')} user={user} /></Suspense></PublicPageWrapper>);
        }
        if (page === 'privacy') {
            return (<PublicPageWrapper><Suspense fallback={<LoadingFallback />}><PrivacyPage onLogin={() => navigate('login')} onBack={() => navigate('dashboard')} /></Suspense></PublicPageWrapper>);
        }
        if (page === 'terms') {
            return (<PublicPageWrapper><Suspense fallback={<LoadingFallback />}><TermsPage onLogin={() => navigate('login')} onBack={() => navigate('dashboard')} /></Suspense></PublicPageWrapper>);
        }
        if (page === 'docs') {
            return (<PublicPageWrapper><Suspense fallback={<LoadingFallback />}><DocsPage onLogin={() => navigate('login')} /></Suspense></PublicPageWrapper>);
        }
        if (page === 'blog') {
            return (<PublicPageWrapper><Suspense fallback={<LoadingFallback />}><BlogPage onLogin={() => navigate('login')} onBack={() => navigate('dashboard')} /></Suspense></PublicPageWrapper>);
        }
        if (page === 'blog-post') {
            return (<PublicPageWrapper><Suspense fallback={<LoadingFallback />}><BlogPostPage onLogin={() => navigate('login')} slug={pageProps.slug} postId={pageProps.postId} /></Suspense></PublicPageWrapper>);
        }
        return (
            <PublicPageWrapper>
                <Suspense fallback={<LoadingFallback />}>
                    <LandingPage
                        onLogin={() => navigate('login')}
                        onNavigate={(newPage) => navigate(newPage)}
                    />
                </Suspense>
            </PublicPageWrapper>
        );
    }

    return (
        <MainLayout
            user={user}
            page={page}
            navigate={navigate}
            isSidebarCollapsed={isSidebarCollapsed}
            setIsSidebarCollapsed={setIsSidebarCollapsed}
            isMobileMenuOpen={isMobileMenuOpen}
            setIsMobileMenuOpen={setIsMobileMenuOpen}
            aiModel={aiModel}
            setAiModel={setAiModel}
            handleLogoutAndNavigate={handleLogoutAndNavigate}
        >
            <Suspense fallback={<LoadingFallback />}>
                {renderPage()}
            </Suspense>
        </MainLayout>
    );
};

export default AppRoutes;
